/**
 * Persistent settings store for the add-in.
 *
 * Uses the taskpane's localStorage. The add-in runs inside an Outlook iframe
 * at a fixed https origin, so this is per-user, per-machine — appropriate for
 * a personal tool. The API key is stored in localStorage; see PLAN.md for the
 * threat model and the optional upgrade path to a secrets backend.
 */
import type { LlmConfig } from "@/types";

const KEY = "bishu.llmConfig.v1";

export const DEFAULT_CONFIG: LlmConfig = {
  baseUrl: "https://api.openai.com/v1",
  apiKey: "",
  model: "gpt-4o-mini",
  temperature: 0.4,
  maxTokens: 0,
  useProxy: true,
};

export function loadConfig(): LlmConfig {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_CONFIG };
    const parsed = JSON.parse(raw) as Partial<LlmConfig>;
    return { ...DEFAULT_CONFIG, ...parsed };
  } catch {
    return { ...DEFAULT_CONFIG };
  }
}

export function saveConfig(cfg: LlmConfig): void {
  localStorage.setItem(KEY, JSON.stringify(cfg));
}

export function clearConfig(): void {
  localStorage.removeItem(KEY);
}

/** Quick check that the config has enough to attempt a request. */
export function isConfigured(cfg: LlmConfig): boolean {
  return Boolean(cfg.baseUrl && cfg.model);
}
